import { Injectable } from '@angular/core';

export interface VacancyMetaInput {
  title?: string;
  city?: string;
  descriptionHtml?: string;
  shortDescription?: string;
}

@Injectable({ providedIn: 'root' })
export class VacancyMetaService {
  /**
   * Build the vacancy meta description snippet.
   *
   * Rules:
   * - Max 155 chars (no ellipsis)
   * - Start: "{baseTitle} in {city}"
   * - Optionally: " | {first sentence after 'Wat ga je doen'}" if that section exists
   * - If final length < 145, append "Solliciteer nu." only if it still fits within 155
   */
  buildMetaDescription(input: VacancyMetaInput): string {
    const title = (input?.title ?? '').trim();
    const city = (input?.city ?? '').trim();

    const baseTitle = this.getBaseTitle(title);
    const prefix = this.normalizeWhitespace(city ? `${baseTitle} in ${city}` : baseTitle);

    const sentence = this.extractWatGaJeDoenFirstSentence(input?.descriptionHtml ?? '');

    let result = prefix;
    const hasExtra = !!sentence;
    if (sentence) {
      result = `${prefix} | ${sentence}`;
    }

    result = this.trimToMax(result, 155);

    // Guard against edge cases where trimming cut right after the pipe.
    result = result.replace(/\s*\|\s*$/, '').trim();

    // If we included a “Wat ga je doen” sentence, enforce a period at the end
    // (while still honoring the 155 char cap).
    if (hasExtra) {
      result = this.ensureEndsWithPeriodWithinMax(result, 155);
    }

    // CTA rule: when the snippet is short enough, append “Solliciteer nu.”
    // If we are appending extra text to the base prefix, use the pipe separator.
    const ctaText = 'Solliciteer nu.';
    const ctaWithSeparator = sentence ? ` ${ctaText}` : ` | ${ctaText}`;
    if (result.length < 145 && (result.length + ctaWithSeparator.length) <= 155) {
      result = `${result}${ctaWithSeparator}`;
    }

    return result;
  }

  /**
   * Full (plain-text) description for JSON-LD. Intentionally NOT the short meta snippet.
   */
  buildJsonLdDescription(input: VacancyMetaInput): string {
    const fromHtml = this.htmlToTextPreservingBreaks(input?.descriptionHtml ?? '');
    const fallback = this.normalizeWhitespace(input?.shortDescription ?? '');
    const text = this.normalizeWhitespace(fromHtml || fallback);

    // JSON-LD can be longer; keep it readable but not excessively whitespacey.
    return text;
  }

  private getBaseTitle(title: string): string {
    const safe = (title ?? '').trim();
    if (!safe) return '';

    // Prefer dash separators with spaces to avoid splitting hyphenated words.
    const dashMatch = safe.match(/\s[-–—]\s/);
    const dashIndex = dashMatch?.index;

    const parenIndex = safe.indexOf('(');

    const candidates = [
      typeof dashIndex === 'number' ? dashIndex : undefined,
      parenIndex >= 0 ? parenIndex : undefined,
    ].filter((n): n is number => typeof n === 'number' && n >= 0);

    if (!candidates.length) return safe;

    const cutIndex = Math.min(...candidates);
    return safe.slice(0, cutIndex).trim();
  }

  private extractWatGaJeDoenFirstSentence(descriptionHtml: string): string {
    const textWithBreaks = this.htmlToTextPreservingBreaks(descriptionHtml);
    if (!textWithBreaks) return '';

    // Tolerant match for heading: "Wat ga je doen" with optional punctuation/spacing.
    const headingRegex = /wat\s*ga\s*je\s*doen\s*[\?:\-–—]*\s*/i;
    const match = headingRegex.exec(textWithBreaks);
    if (!match) return '';

    const after = textWithBreaks.slice(match.index + match[0].length);
    const cleanedAfter = after.replace(/^[\s\n\r\t\-|–—:;]+/, '');

    // Prefer the first meaningful "chunk" (paragraph / first bullet line) to avoid
    // bleeding into the 2nd bullet like: "...architecturen; - Inrichten van".
    const firstChunk = this.pickFirstChunk(cleanedAfter);
    const firstSentence = this.pickFirstSentence(firstChunk);
    if (!firstSentence) return '';

    return this.ensureSentenceEndsWithPeriod(firstSentence);
  }

  private pickFirstChunk(text: string): string {
    const raw = (text ?? '').trim();
    if (!raw) return '';

    // Split on blank lines to get a "paragraph".
    const paragraphs = raw.split(/\n\s*\n+/g).map(p => p.trim()).filter(Boolean);
    const candidate = paragraphs[0] ?? raw;

    // If it looks like a bullet list, use the first bullet line.
    const lines = candidate.split(/\n+/g).map(l => l.trim()).filter(Boolean);
    const firstLine = lines[0] ?? candidate;

    return firstLine
      .replace(/^[-–—•\u2022]+\s+/, '')
      .trim();
  }

  private pickFirstSentence(text: string): string {
    const normalized = this.normalizeWhitespace(text);
    if (!normalized) return '';

    // Find the earliest sentence terminator, but be tolerant of Dutch abbreviations
    // like "o.a." and "d.w.z." so we don't stop at "o.".
    // We treat ';' as an acceptable terminator because some sources use it to
    // separate bullet-like fragments.
    const endIdx = this.findFirstSentenceTerminatorIndex(normalized);
    let candidate = normalized;

    if (endIdx >= 0) {
      candidate = normalized.slice(0, endIdx + 1);
    } else {
      // No terminator: take a reasonable chunk.
      candidate = normalized.slice(0, 220);
    }

    candidate = candidate.trim();

    // Strip trailing punctuation like ';' and normalize ending.
    candidate = candidate.replace(/[\s\-–—,;:]+$/, '');

    // Remove any trailing bullet marker if it slipped in.
    candidate = candidate.replace(/\s+[-–—•\u2022]\s*$/, '');
    return candidate;
  }

  private findFirstSentenceTerminatorIndex(text: string): number {
    const s = text ?? '';
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch !== '.' && ch !== '!' && ch !== '?' && ch !== ';') continue;

      // Don't stop on '.' inside common dot-abbreviations like o.a., d.w.z., i.v.m.
      if (ch === '.' && this.isDotInAbbreviation(s, i)) {
        continue;
      }

      return i;
    }
    return -1;
  }

  private isDotInAbbreviation(s: string, dotIndex: number): boolean {
    if (dotIndex < 0 || dotIndex >= s.length) return false;
    if (s[dotIndex] !== '.') return false;

    const isAlphaNum = (c: string | undefined) => !!c && /[0-9A-Za-zÀ-ÖØ-öø-ÿ]/.test(c);

    const prev = s[dotIndex - 1];
    const next = s[dotIndex + 1];
    const nextNext = s[dotIndex + 2];
    const prevPrev = s[dotIndex - 2];
    const prevPrevPrev = s[dotIndex - 3];

    // Pattern: letter '.' letter '.' (first dot)
    const isFirstDot = isAlphaNum(prev) && isAlphaNum(next) && nextNext === '.';
    // Pattern: letter '.' letter '.' (second dot)
    const isSecondDot = isAlphaNum(prevPrevPrev) && prevPrev === '.' && isAlphaNum(prev);

    if (!isFirstDot && !isSecondDot) return false;

    // If the dot is the last non-space char, allow it to terminate.
    const rest = s.slice(dotIndex + 1);
    if (!rest.trim()) return false;

    // If there's more text after this abbreviation dot, it's not a sentence end.
    return true;
  }

  private ensureSentenceEndsWithPeriod(sentence: string): string {
    let s = this.normalizeWhitespace(sentence);
    if (!s) return '';

    // Remove trailing punctuation and force a single '.'
    s = s.replace(/[\s\-–—,;:]+$/, '');
    if (/[!?]$/.test(s)) {
      s = s.replace(/[!?]+$/, '');
    }
    if (!s.endsWith('.')) {
      s = `${s}.`;
    }
    return s;
  }

  private ensureEndsWithPeriodWithinMax(text: string, maxLen: number): string {
    const t = this.normalizeWhitespace(text);
    if (!t) return '';

    if (t.endsWith('.')) return t;
    if (t.length < maxLen) return `${t}.`;

    // At max length already: try replacing the last character with a period.
    const trimmed = t.replace(/[\s\-–—,;:]+$/, '');
    if (trimmed.endsWith('.')) return trimmed;
    if (trimmed.length === 0) return t;
    return `${trimmed.slice(0, Math.max(0, maxLen - 1))}.`;
  }

  private trimToMax(text: string, maxLen: number): string {
    const t = this.normalizeWhitespace(text);
    if (t.length <= maxLen) return t;

    const cut = t.slice(0, maxLen);
    const lastSpace = cut.lastIndexOf(' ');

    // Keep at least some meaningful content when cutting on word boundaries.
    const clipped = (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim();

    return clipped.replace(/[\s\-–—,;:|]+$/, '').trim();
  }

  private normalizeWhitespace(text: string): string {
    return (text ?? '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Convert HTML-ish input into text, trying to keep section boundaries.
   * This is intentionally conservative and browser-friendly.
   */
  private htmlToTextPreservingBreaks(html: string): string {
    const raw = (html ?? '').trim();
    if (!raw) return '';

    let s = raw;

    // Drop scripts/styles.
    s = s.replace(/<script[\s\S]*?<\/script>/gi, ' ');
    s = s.replace(/<style[\s\S]*?<\/style>/gi, ' ');

    // Insert breaks for common block elements.
    s = s.replace(/<br\s*\/?\s*>/gi, '\n');
    s = s.replace(/<\/(p|div|h[1-6]|li|ul|ol|section|article|blockquote|tr)>/gi, '\n');

    // List items: keep as a dash.
    s = s.replace(/<li[^>]*>/gi, '- ');

    // Strip remaining tags.
    s = s.replace(/<[^>]+>/g, ' ');

    // Decode entities.
    s = this.decodeHtmlEntities(s);

    // Normalize whitespace but keep some newlines for heading scanning.
    s = s.replace(/\r/g, '');
    s = s.replace(/[ \t]+\n/g, '\n');
    s = s.replace(/\n[ \t]+/g, '\n');
    s = s.replace(/\n{3,}/g, '\n\n');

    return s.trim();
  }

  private decodeHtmlEntities(text: string): string {
    const input = text ?? '';

    // Browser path
    if (typeof document !== 'undefined' && document?.createElement) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = input;
      return textarea.value;
    }

    // Fallback (best-effort)
    return input
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
  }
}
